import type {
  IManufacturingOrder,
  IWorkOrder,
  IDefect,
  IQualityCheckpoint,
  IMaterialConsumption,
  ICreateMORequest,
  IUpdateMORequest,
  IUpdateMOStatusRequest,
  IUpdateWORequest,
  IStartWORequest,
  ICompleteWORequest,
  IAssignOperatorRequest,
  ICreateDefectRequest,
  IUpdateDefectRequest,
  ICreateCheckpointRequest,
  IRecordMaterialRequest,
  IMOFilter,
  MOStatus,
} from '../types/mes';
import { get, post, put, buildUrl } from './apiClient';

const API_BASE = '/api/v1/mes';

// ============================================================================
// Manufacturing Order API
// ============================================================================

export async function listManufacturingOrders(filter?: IMOFilter): Promise<IManufacturingOrder[]> {
  return get<IManufacturingOrder[]>(buildUrl(`${API_BASE}/orders`, filter));
}

export async function getManufacturingOrder(moId: string): Promise<IManufacturingOrder> {
  return get<IManufacturingOrder>(`${API_BASE}/orders/${moId}`);
}

export async function createManufacturingOrder(request: ICreateMORequest): Promise<IManufacturingOrder> {
  return post<IManufacturingOrder>(`${API_BASE}/orders`, request);
}

export async function updateManufacturingOrder(moId: string, request: IUpdateMORequest): Promise<IManufacturingOrder> {
  return put<IManufacturingOrder>(`${API_BASE}/orders/${moId}`, request);
}

export async function updateMOStatus(moId: string, status: MOStatus): Promise<IManufacturingOrder> {
  const request: IUpdateMOStatusRequest = { status };
  return put<IManufacturingOrder>(`${API_BASE}/orders/${moId}/status`, request);
}

export async function releaseMO(moId: string): Promise<IManufacturingOrder> {
  return post<IManufacturingOrder>(`${API_BASE}/orders/${moId}/release`);
}

// ============================================================================
// Work Order API
// ============================================================================

export async function listWorkOrders(moId: string): Promise<IWorkOrder[]> {
  return get<IWorkOrder[]>(`${API_BASE}/orders/${moId}/workorders`);
}

export async function getWorkOrder(woId: string): Promise<IWorkOrder> {
  return get<IWorkOrder>(`${API_BASE}/workorders/${woId}`);
}

export async function updateWorkOrder(woId: string, request: IUpdateWORequest): Promise<IWorkOrder> {
  return put<IWorkOrder>(`${API_BASE}/workorders/${woId}`, request);
}

export async function startWorkOrder(woId: string, operatorId: string): Promise<IWorkOrder> {
  const request: IStartWORequest = { operator_id: operatorId };
  return post<IWorkOrder>(`${API_BASE}/workorders/${woId}/start`, request);
}

export async function completeWorkOrder(woId: string, completedQty: number, scrappedQty: number): Promise<IWorkOrder> {
  const request: ICompleteWORequest = {
    completed_qty: completedQty,
    scrapped_qty: scrappedQty,
  };
  return post<IWorkOrder>(`${API_BASE}/workorders/${woId}/complete`, request);
}

export async function assignOperator(woId: string, request: IAssignOperatorRequest): Promise<IWorkOrder> {
  return post<IWorkOrder>(`${API_BASE}/workorders/${woId}/operators`, request);
}

// ============================================================================
// Defect API
// ============================================================================

export async function listDefects(woId: string): Promise<IDefect[]> {
  return get<IDefect[]>(`${API_BASE}/workorders/${woId}/defects`);
}

export async function createDefect(woId: string, request: ICreateDefectRequest): Promise<IDefect> {
  return post<IDefect>(`${API_BASE}/workorders/${woId}/defects`, request);
}

export async function updateDefect(defectId: string, request: IUpdateDefectRequest): Promise<IDefect> {
  return put<IDefect>(`${API_BASE}/defects/${defectId}`, request);
}

// ============================================================================
// Quality Checkpoint API
// ============================================================================

export async function listCheckpoints(woId: string): Promise<IQualityCheckpoint[]> {
  return get<IQualityCheckpoint[]>(`${API_BASE}/workorders/${woId}/checkpoints`);
}

export async function createCheckpoint(woId: string, request: ICreateCheckpointRequest): Promise<IQualityCheckpoint> {
  return post<IQualityCheckpoint>(`${API_BASE}/workorders/${woId}/checkpoints`, request);
}

// ============================================================================
// Material Consumption API
// ============================================================================

export async function listMaterials(woId: string): Promise<IMaterialConsumption[]> {
  return get<IMaterialConsumption[]>(`${API_BASE}/workorders/${woId}/materials`);
}

export async function recordMaterial(woId: string, request: IRecordMaterialRequest): Promise<IMaterialConsumption> {
  return post<IMaterialConsumption>(`${API_BASE}/workorders/${woId}/materials`, request);
}
